import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MovieIcon from "@mui/icons-material/Movie";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useGenerationNotifications } from "../app/context/GenerationNotificationContext";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
  homeGlassHero,
  homeGlassShadow,
  homeGlassSilo,
} from "../pages/Home/constants/homeGlass";

const BUBBLE_SIZE = 48;
const EDGE_GAP = 14;
const PANEL_WIDTH = 286;
const PANEL_MAX_HEIGHT = 220;
const PANEL_GAP = 10;
const FLOATING_GLASS_SHADOW =
  "inset 0 1px 0 rgba(255,255,255,0.32), 0 18px 42px rgba(0,0,0,0.28)";

const getDefaultPosition = () => {
  if (typeof window === "undefined") {
    return { x: EDGE_GAP, y: EDGE_GAP };
  }

  return {
    x: Math.max(EDGE_GAP, window.innerWidth - BUBBLE_SIZE - 24),
    y: Math.max(EDGE_GAP, window.innerHeight - BUBBLE_SIZE - 88),
  };
};

const clampPosition = (position) => {
  if (typeof window === "undefined") return position;

  return {
    x: Math.min(
      Math.max(EDGE_GAP, position.x),
      window.innerWidth - BUBBLE_SIZE - EDGE_GAP,
    ),
    y: Math.min(
      Math.max(EDGE_GAP, position.y),
      window.innerHeight - BUBBLE_SIZE - EDGE_GAP,
    ),
  };
};

const formatNotificationTitle = (notification) => {
  const mediaLabel = notification.mediaType === "video" ? "Video" : "Image";

  if (notification.status === "pending") return `Creating ${mediaLabel.toLowerCase()}`;
  if (notification.status === "failed") return `${mediaLabel} generation failed`;
  return `${mediaLabel} is ready`;
};

const formatNotificationTime = (value) => {
  if (!value) return "";

  return new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function GenerationNotificationBubble() {
  const navigate = useNavigate();
  const {
    notifications,
    markGenerationRead,
    removeGenerationNotification,
  } = useGenerationNotifications();

  const [position, setPosition] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const closePanelTimerRef = useRef(null);

  const completedVideoNotifications = useMemo(
    () =>
      notifications.filter(
        (item) => item.status === "completed" && item.mediaType === "video",
      ),
    [notifications],
  );
  const completedVideoUnreadCount = completedVideoNotifications.filter(
    (item) => item.unread,
  ).length;
  const visible = completedVideoNotifications.length > 0;

  useEffect(() => {
    setPosition((prev) => prev ?? getDefaultPosition());

    const handleResize = () => {
      setPosition((prev) => (prev ? clampPosition(prev) : getDefaultPosition()));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(
    () => () => {
      if (closePanelTimerRef.current) {
        window.clearTimeout(closePanelTimerRef.current);
      }
    },
    [],
  );

  if (!visible || !position) return null;

  const estimatedPanelHeight = Math.min(
    PANEL_MAX_HEIGHT,
    completedVideoNotifications.length * 82 + 16,
  );

  const panelLeft = (() => {
    if (typeof window === "undefined") return EDGE_GAP;

    const maxLeft = Math.max(EDGE_GAP, window.innerWidth - PANEL_WIDTH - EDGE_GAP);
    const rightSide = position.x > window.innerWidth / 2;
    if (rightSide) {
      return Math.min(
        Math.max(EDGE_GAP, position.x + BUBBLE_SIZE - PANEL_WIDTH),
        maxLeft,
      );
    }

    return Math.min(Math.max(EDGE_GAP, position.x), maxLeft);
  })();

  const panelTop = (() => {
    if (typeof window === "undefined") return EDGE_GAP;

    const maxTop = Math.max(
      EDGE_GAP,
      window.innerHeight - estimatedPanelHeight - EDGE_GAP,
    );
    const aboveBubble = position.y - estimatedPanelHeight - PANEL_GAP;
    const belowBubble = position.y + BUBBLE_SIZE + PANEL_GAP;
    const preferredTop = aboveBubble >= EDGE_GAP ? aboveBubble : belowBubble;

    return Math.min(Math.max(EDGE_GAP, preferredTop), maxTop);
  })();

  const handlePointerDown = (event) => {
    if (event.button !== 0) return;

    setPanelOpen(false);
    if (closePanelTimerRef.current) {
      window.clearTimeout(closePanelTimerRef.current);
      closePanelTimerRef.current = null;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    const movement = Math.hypot(
      event.clientX - dragRef.current.startX,
      event.clientY - dragRef.current.startY,
    );
    if (movement > 4) {
      if (!dragRef.current.moved) {
        setIsDragging(true);
        setPanelOpen(false);
      }
      dragRef.current.moved = true;
    }

    setPosition(
      clampPosition({
        x: event.clientX - dragRef.current.offsetX,
        y: event.clientY - dragRef.current.offsetY,
      }),
    );
  };

  const handlePointerUp = (event) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) {
      return;
    }

    const wasDragging = dragRef.current.moved;

    suppressClickRef.current = wasDragging;
    event.currentTarget.releasePointerCapture(event.pointerId);
    dragRef.current = null;
    setIsDragging(false);
  };

  const handleBubbleClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }

    setPanelOpen((prev) => !prev);
  };

  const handleOpenNotification = (notification) => {
    markGenerationRead(notification.id);
    setPanelOpen(false);
    navigate(notification.route || "/generate/chatgpt", {
      state: notification.conversationId
        ? {
            openGenerationConversationId: notification.conversationId,
            openGenerationPrompt: notification.prompt,
          }
        : undefined,
    });
  };

  const handleDismissNotification = (notificationId) => {
    removeGenerationNotification(notificationId);
    if (completedVideoNotifications.length <= 1) {
      setPanelOpen(false);
    }
  };

  const openPanel = () => {
    if (isDragging || dragRef.current) return;
    if (closePanelTimerRef.current) {
      window.clearTimeout(closePanelTimerRef.current);
      closePanelTimerRef.current = null;
    }
    setPanelOpen(true);
  };

  const closePanelSoon = () => {
    if (closePanelTimerRef.current) {
      window.clearTimeout(closePanelTimerRef.current);
    }
    closePanelTimerRef.current = window.setTimeout(() => {
      setPanelOpen(false);
      closePanelTimerRef.current = null;
    }, 180);
  };

  return (
    <>
      <Tooltip title={panelOpen || isDragging ? "" : "Generated video ready"}>
        <Box
          onMouseEnter={openPanel}
          onMouseLeave={closePanelSoon}
          onFocus={openPanel}
          sx={{
            position: "fixed",
            left: position.x,
            top: position.y,
            zIndex: 1600,
            touchAction: "none",
          }}
        >
          <Badge
            badgeContent={completedVideoUnreadCount}
            color="error"
            invisible={completedVideoUnreadCount === 0}
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                top: 4,
                right: 4,
                minWidth: 18,
                height: 18,
                borderRadius: "999px",
                fontSize: 10,
                fontWeight: 800,
                color: "#fff",
                background:
                  "linear-gradient(135deg, rgba(255,116,116,0.98), rgba(255,84,132,0.92))",
                border: "1px solid rgba(255,255,255,0.72)",
                boxShadow:
                  "0 0 0 3px rgba(255,255,255,0.12), 0 8px 18px rgba(255,84,132,0.26)",
              },
            }}
          >
            <IconButton
              aria-label="Generated video notifications"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onClick={handleBubbleClick}
              sx={{
                width: BUBBLE_SIZE,
                height: BUBBLE_SIZE,
                color: "#fff",
                cursor: "grab",
                background: homeGlassSilo.active,
                border: "1px solid rgba(255,255,255,0.42)",
                backdropFilter: HOME_GLASS_BACKDROP,
                WebkitBackdropFilter: HOME_GLASS_BACKDROP,
                boxShadow: FLOATING_GLASS_SHADOW,
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: 6,
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle at 32% 24%, rgba(255,255,255,0.52), rgba(255,255,255,0.1) 48%, transparent 72%)",
                  pointerEvents: "none",
                },
                animation:
                  completedVideoUnreadCount > 0
                    ? "generationBubblePulse 1.6s ease-in-out infinite"
                    : "none",
                "@keyframes generationBubblePulse": {
                  "0%, 100%": {
                    transform: "scale(1)",
                    boxShadow: FLOATING_GLASS_SHADOW,
                  },
                  "50%": {
                    transform: "scale(1.05)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.38), 0 20px 54px rgba(255,255,255,0.18), 0 16px 34px rgba(0,0,0,0.3)",
                  },
                },
                "&:active": { cursor: "grabbing" },
                "&:hover": {
                  background: homeGlassSilo.activeHover,
                  transform: "translateY(-1px)",
                },
              }}
            >
              <NotificationsActiveIcon
                sx={{ position: "relative", zIndex: 1, fontSize: 23 }}
              />
            </IconButton>
          </Badge>
        </Box>
      </Tooltip>

      {panelOpen && (
        <Paper
          elevation={0}
          onMouseEnter={openPanel}
          onMouseLeave={closePanelSoon}
          sx={{
            position: "fixed",
            left: panelLeft,
            top: panelTop,
            width: `min(${PANEL_WIDTH}px, calc(100vw - 28px))`,
            maxHeight: `min(${PANEL_MAX_HEIGHT}px, calc(100vh - 28px))`,
            "@media (max-width: 767px)": {
              maxHeight: `min(${PANEL_MAX_HEIGHT}px, calc(100dvh - 28px))`,
            },
            zIndex: 1599,
            overflow: "hidden",
            borderRadius: "22px",
            color: "#fff",
            background:
              "linear-gradient(145deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08))",
            border: HOME_GLASS_BORDER,
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.28), 0 24px 70px rgba(0,0,0,0.34)",
            backdropFilter: HOME_GLASS_BACKDROP,
            WebkitBackdropFilter: HOME_GLASS_BACKDROP,
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at top left, rgba(255,255,255,0.22), transparent 38%)",
              pointerEvents: "none",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              maxHeight: PANEL_MAX_HEIGHT - 16,
              overflowY: "auto",
              p: 0.75,
            }}
          >
            {completedVideoNotifications.map((notification) => {
              return (
                <Box
                  key={notification.id}
                  sx={{
                    display: "flex",
                    gap: 1,
                    p: 0.9,
                    borderRadius: "16px",
                    background: "transparent",
                  }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 44,
                      flexShrink: 0,
                      borderRadius: "14px",
                      overflow: "hidden",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.26), rgba(255,255,255,0.08))",
                      border: "1px solid rgba(255,255,255,0.2)",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.28)",
                    }}
                  >
                    <MovieIcon sx={{ color: "rgba(255,255,255,0.9)", fontSize: 23 }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#fff",
                        }}
                      >
                        {formatNotificationTitle(notification)}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.4,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            color: "rgba(255,255,255,0.48)",
                          }}
                        >
                          {formatNotificationTime(notification.updatedAt)}
                        </Typography>
                        <IconButton
                          aria-label="Dismiss video notification"
                          size="small"
                          onClick={() => handleDismissNotification(notification.id)}
                          sx={{
                            width: 22,
                            height: 22,
                            color: "rgba(255,255,255,0.62)",
                            "&:hover": {
                              color: "#fff",
                              background: "rgba(255,255,255,0.1)",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Box>
                    </Box>

                    <Typography
                      title={notification.prompt}
                      sx={{
                        mt: 0.25,
                        fontSize: 11,
                        color: "rgba(255,255,255,0.64)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {notification.prompt || "Generated video"}
                    </Typography>

                    <Button
                      size="small"
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      onClick={() => handleOpenNotification(notification)}
                      sx={{
                        mt: 0.65,
                        px: 1,
                        minHeight: 26,
                        fontSize: 12,
                        color: "#fff",
                        borderRadius: "999px",
                        textTransform: "none",
                        fontWeight: 700,
                        background: homeGlassHero.rest,
                        border: "1px solid rgba(255,255,255,0.16)",
                        boxShadow: homeGlassShadow.rest,
                        "&:hover": { background: homeGlassHero.hover },
                      }}
                    >
                      Open video
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}
    </>
  );
}
